import dayjs from 'dayjs';
import { CoachRow, CenterAvg, MemberStage, STAGE_META } from './tabData';
import {
    METRICS,
    MetricDef,
    MetricKey,
    MetricConfidence,
    MIN_ACTIVE_MEMBERS_FOR_JUDGEMENT,
    MIN_LESSONS_FOR_JUDGEMENT,
    checkSample,
    judgementMembers,
    densityMembers,
    membersLabelOf,
    lessonDensityAvgOf,
} from './coachAnalysis';
import { MemberView, pickPrimaryCards } from './adapters';
import { REREGISTRATION_RATE_BY_ORDER, FALLBACK_REREGISTRATION_RATE } from './memberValue';
import { FIRST_PASS_CHURN_RATE, watchReasons, WATCH_REASON_TEXT } from './passOrder';
import { isSessionless } from './sessionless';
import { OrderDto } from '../../types/opsBriefing';

/**
 * 월간 강사 리포트 구성 엔진.
 *
 * ─────────────────────────────────────────────────────────────
 * 이 파일이 푸는 문제
 * ─────────────────────────────────────────────────────────────
 * 예전 리포트는 숫자를 나열했습니다. "전환율 25%, 일지 95%, 노쇼 4.2%".
 * 원장이 그걸 들고 강사 앞에 앉으면 할 수 있는 말이 "숫자가 이렇네요" 뿐입니다.
 * 대화가 안 됩니다.
 *
 * 리포트가 해야 하는 일은 셋입니다.
 *   ① 숫자를 뜻으로 바꾼다        "4.2%" → "센터 평균의 4.7배"
 *   ② 물어볼 것을 만들어 준다      "왜 그런가요?"가 아니라 구체적인 질문
 *   ③ 합의할 것을 남긴다          다음 달에 무엇을 볼지 둘이 정한다
 *
 * ─────────────────────────────────────────────────────────────
 * 데이터가 적은 센터를 위한 구성
 * ─────────────────────────────────────────────────────────────
 * 이제 막 도입한 센터는 표본이 없습니다. 그런데도 평균 대비 순위를 뽑으면
 * 우연히 나온 숫자로 사람을 평가하게 됩니다. 세 명 중 꼴찌라는 말은
 * 세 명뿐일 때 아무 뜻이 없습니다.
 *
 * 그래서 표본 수에 따라 **리포트 구성 자체를 바꿉니다.**
 *   RICH  비교·순위·개선점까지 정상 구성
 *   THIN  비교를 빼고 '관찰된 사실'과 '다음 달 관찰 계획'으로
 *   NEW   지표 대신 '무엇부터 쌓을 것인가'로
 *
 * ⚠️ 이 판단은 규칙 기반입니다. 생성형 AI가 문장을 짓지 않습니다.
 *    사람 평가에 쓰이는 문서라 "그럴듯하지만 틀린 문장"이 들어가면 안 됩니다.
 *    모든 문장은 실제 숫자에서 기계적으로 유도됩니다.
 */

/* ═══════════════════════════════════════════════════════════
   데이터 성숙도
   ═══════════════════════════════════════════════════════════ */

export type Maturity = 'RICH' | 'THIN' | 'NEW';

/**
 * 비교를 시작할 수 있는 최소 담당 회원 수.
 *
 * ⚠️ 값은 coachAnalysis 에 있습니다. 여기서 숫자를 다시 적지 마세요 —
 * 화면(강사 현황 등급·사분면)과 인쇄물이 **같은 기준**을 써야 합니다.
 * 예전에는 이 기준이 여기에만 있어서, 담당 1명짜리 강사가 화면에서는
 * '핵심 인재'이고 같은 날 뽑은 PDF 에서는 '판단 보류'였습니다.
 */
export const MIN_MEMBERS_FOR_COMPARE = MIN_ACTIVE_MEMBERS_FOR_JUDGEMENT;
/** 판정에 쓸 수 있는 지표가 이만큼은 있어야 종합 평가가 성립한다 */
export const MIN_JUDGEABLE_METRICS = 2;

/**
 * 비교를 접은 이유 — 기계가 읽는 값.
 *
 * `note` 는 사람이 읽는 긴 문장이라, 화면이 짧은 문구를 고르려고 그 문장을
 * 문자열 검색하면 문장을 다듬는 순간 조용히 깨집니다.
 * NA 로 떨어지는 이유가 둘 이상이라(표본 부족 · 센터 평균 없음) 이 구분이
 * 필요합니다 — GRADE_META.NA 주석 참고.
 */
export type MaturityReason =
    | 'NONE'
    /** 수업도 회원도 아예 없다 */
    | 'NO_RECORD'
    /** 담당 활성 회원이 기준 미만 */
    | 'THIN_MEMBERS'
    /** 집계 기간 수업이 기준 미만 */
    | 'THIN_LESSONS'
    /** 센터에 비교할 다른 강사가 없다 */
    | 'ALONE'
    /** 맞대볼 지표 자체가 모자라다 (센터 평균이 없는 경우 포함) */
    | 'FEW_METRICS';

export type MaturityResult = {
    level: Maturity;
    /** 리포트 첫 장에 그대로 싣는 안내 */
    note: string;
    /** 비교·순위를 써도 되는가 */
    canCompare: boolean;
    reason: MaturityReason;
};

/** 표본이 모자라서 접은 것인가 — '평가 유보'라고 부를 수 있는 경우 */
export const isSampleShortage = (m: MaturityResult): boolean =>
    m.reason === 'NO_RECORD' || m.reason === 'THIN_MEMBERS' || m.reason === 'THIN_LESSONS';

export const resolveMaturity = (
    coach: CoachRow,
    judgeableCount: number,
    coachCount: number
): MaturityResult => {
    /* 🔴 분모는 `judgementMembers` — 밀도·표본 판정과 같은 값이다 (월 스코프) */
    if (judgementMembers(coach) === 0 || coach.lessons === 0) {
        return {
            level: 'NEW',
            note: '아직 수업 기록이 쌓이지 않아 성과를 평가할 수 없습니다. 이번 리포트는 평가 대신 무엇부터 쌓을지를 정리했습니다.',
            canCompare: false,
            reason: 'NO_RECORD',
        };
    }
    /*
     * 비교를 뺀 이유를 정확히 적는다.
     *
     * 예전에는 세 조건 중 무엇에 걸리든 "담당 회원 N명 기준이라 표본이 적습니다"로
     * 나갔다. 서버가 centerAvg 를 안 보내 걸린 경우에는 담당 회원이 12명이어도
     * 그 문장이 인쇄돼, 원장이 있지도 않은 문제(회원 수)를 붙들게 된다.
     * 이 문장은 PDF 1장 '이 리포트를 읽는 방법'에 그대로 실린다.
     */
    const tail = '순위와 우열 판단은 넣지 않았고, 지금 확인된 사실과 다음 달에 볼 것만 담았습니다.';
    if (judgementMembers(coach) < MIN_MEMBERS_FOR_COMPARE) {
        return {
            level: 'THIN',
            note: `${membersLabelOf(coach)} ${judgementMembers(coach)}명 기준이라 센터 평균과 비교하기에는 표본이 적습니다. ${tail}`,
            canCompare: false,
            reason: 'THIN_MEMBERS',
        };
    }
    /*
     * 수업 수도 본다.
     *
     * 예전에는 회원 수만 봤다. 담당 6명인데 이번 달 수업이 3회인 강사는
     * 이 관문을 그대로 통과했고, 일지 작성률·노쇼율이 3회에서 나온 100%·0% 라
     * 강점 세 개가 잡혀 **S 등급이 인쇄**될 수 있었다.
     * 일지·노쇼의 분모는 회원 수가 아니라 수업·예약 수다
     * (MIN_LESSONS_FOR_JUDGEMENT 주석에 눈금 계산이 있다).
     */
    if (coach.lessons < MIN_LESSONS_FOR_JUDGEMENT) {
        return {
            level: 'THIN',
            note:
                `이번 기간 수업이 ${coach.lessons}회뿐이라 일지·노쇼 같은 비율 지표를 ` +
                `믿을 수 없습니다. 수업 한 번이 비율을 크게 흔듭니다. ${tail}`,
            canCompare: false,
            reason: 'THIN_LESSONS',
        };
    }
    if (coachCount < 2) {
        return {
            level: 'THIN',
            note: `센터에 비교할 다른 강사가 없어 센터 평균이 곧 이 강사 자신입니다. ${tail}`,
            canCompare: false,
            reason: 'ALONE',
        };
    }
    if (judgeableCount < MIN_JUDGEABLE_METRICS) {
        return {
            level: 'THIN',
            note:
                `센터 평균과 맞대볼 수 있는 지표가 ${judgeableCount}개뿐입니다. ` +
                `기준이 아직 집계되지 않았거나 이 강사의 값이 비어 있는 지표입니다. ${tail}`,
            canCompare: false,
            reason: 'FEW_METRICS',
        };
    }
    return {
        level: 'RICH',
        note: '센터 평균과 비교할 수 있을 만큼 기록이 쌓였습니다.',
        canCompare: true,
        reason: 'NONE',
    };
};

/* ═══════════════════════════════════════════════════════════
   지표 읽기 — 숫자를 뜻으로
   ═══════════════════════════════════════════════════════════ */

export type MetricVerdict = 'GOOD' | 'BAD' | 'FLAT' | 'UNKNOWN';

/**
 * 비율의 분자·분모를 지표 키로 찾아 준다.
 * 서버가 안 보내면(구버전) null — 없는 걸 만들어내지 않는다.
 */
export const basisOf = (
    coach: CoachRow,
    key: MetricKey
): { numerator: number; denominator: number } | null => {
    const b = coach.basis;
    if (!b) return null;
    switch (key) {
        case 'conversion':
            return b.conversion ?? null;
        case 'closing':
            return b.closing ?? null;
        case 'journal':
            return b.journal ?? null;
        case 'noshow':
            return b.noshow ?? null;
        default:
            return null;
    }
};

export type ReportMetric = {
    key: MetricKey;
    label: string;
    unit: string;
    value: number | null;
    avg: number | null;
    higherIsBetter: boolean;
    confidence: MetricConfidence;
    /** 평균 대비 몇 %인가. 기준이 없으면 null */
    vsAvgPct: number | null;
    /** 몇 배인가 — '4.7배'가 '367% 높음'보다 훨씬 빨리 읽힌다 */
    ratio: number | null;
    verdict: MetricVerdict;
    /** 숫자를 사람 말로 옮긴 한 줄 */
    reading: string;
    /** 판정 근거로 쓸 수 있는가 (REFERENCE 지표는 false) */
    judgeable: boolean;
    /** 센터 내 순위 (표본 있는 강사 중). 비교 불가면 null */
    rank: number | null;
    rankTotal: number | null;
    /** 지난달 값 */
    prev: number | null;
    /** 지난달 대비 변화량 (같은 단위). 지난달이 없으면 null */
    delta: number | null;
    /** 변화가 좋아진 쪽인가 */
    improved: boolean | null;
    /**
     * 비율의 근거 — `19/60`.
     * 31.7% 만 보면 표본이 3명인지 300명인지 알 수 없습니다.
     * 못 재는 지표거나 비율이 아닌 지표(수업 진행량 등)는 null.
     */
    basis?: { numerator: number; denominator: number } | null;
    /** 이 지표가 왜 중요한가 — 원장이 숫자의 뜻을 알아야 강사와 대화가 된다 */
    why?: string;
};

/** 평균 대비 이만큼 벌어져야 의미 있는 차이로 본다 */
export const MEANINGFUL_GAP = 0.15;

const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * 숫자를 뜻으로 옮긴다.
 *
 * "4.2%"는 아무 뜻이 없습니다. "센터 평균의 4.7배"여야 읽힙니다.
 * 비교 기준이 없으면 배수를 만들지 않고 관찰값만 적습니다 —
 * 없는 기준으로 지어낸 배수가 면담 자리에 올라가면 안 됩니다.
 */
/** 지난달 값 꺼내기 — 계약에 있는 것만 */
const prevOf = (coach: CoachRow, key: MetricKey): number | null => {
    const p = coach.prev;
    if (!p) return null;
    switch (key) {
        case 'conversion':
            return p.conversionPct;
        case 'closing':
            return p.closingRate;
        case 'journal':
            return p.journalPct;
        case 'lessons':
            return p.lessons;
        case 'lessonDensity':
            /*
             * 지난달 밀도 — 분모도 **그 강사의 판정 분모**를 쓴다.
             * ⚠️ 엄밀히는 「지난달 담당 수」로 나눠야 맞지만 서버가 지난달 분모를
             *    안 준다(prev 에 lessons 만 있다). 이번 달 분모로 나눈 근사치이고,
             *    그래도 옛 코드(오늘 기준 활성 회원)보다는 가깝다.
             */
            /*
             * 🆕 분모도 밀도 분모를 쓴다 — 이번 달 값과 같은 자여야 「지난달 대비」가
             *    뜻을 갖는다.
             *
             * 🔴 **`null` 과 `undefined` 를 구분한다.**
             *      `undefined` 서버가 지난달 분모를 아예 안 보내는 세대다 →
             *                  이번 달 분모로 나눈 **근사치**를 쓴다(옛 동작).
             *      `null`      서버가 「지난달은 못 쟀다」고 밝힌 것이다 →
             *                  이번 달 분모로 대신 나누면 **없는 지난달 밀도를 지어낸다.**
             *                  그 값이 03 표의 「지난달」 칸과 「전월 대비」에 그대로
             *                  인쇄되고, 면담에서 「지난달보다 떨어졌네요」의 근거가 된다.
             *                  못 잰 것은 못 쟀다고 둔다.
             */
            if (p.lessonMembers === null) return null;
            /* 이번 달 분모도 못 쟀으면(`densityMembers` null) 근사할 자가 없다 */
            const prevDenom = p.lessonMembers ?? densityMembers(coach);
            return p.lessons != null && prevDenom != null && prevDenom > 0
                ? round1(p.lessons / prevDenom)
                : null;
        default:
            return null;
    }
};

/**
 * 센터 내 순위.
 * 값이 없는 강사는 순위에서 뺍니다 — null 을 0으로 두면 꼴찌로 잡힙니다.
 */
export const rankOf = (
    metric: MetricDef,
    coach: CoachRow,
    peers: CoachRow[]
): { rank: number | null; total: number | null } => {
    const measured = peers
        /*
         * 표본이 모자란 동료는 순위에서 뺀다.
         *
         * 담당 1명짜리 강사의 전환율 100% 가 순위에 끼면 정상 강사 전원이
         * 한 칸씩 밀린다. "센터 8명 중 4위"가 인쇄물에 찍히는데 그 1위가
         * 회원 한 명이 재등록한 결과라면 그 순위는 근거가 못 된다.
         * (본인이 유보면 애초에 canCompare 가 false 라 순위 자체를 안 만든다)
         */
        .filter((c) => checkSample(c).enough)
        .map((c) => ({ c, v: metric.get(c) }))
        .filter((x): x is { c: CoachRow; v: number } => x.v != null)
        .sort((a, b) => (metric.higherIsBetter ? b.v - a.v : a.v - b.v));

    const idx = measured.findIndex((x) => x.c.coachId === coach.coachId);
    if (idx < 0 || measured.length < 2) return { rank: null, total: null };
    return { rank: idx + 1, total: measured.length };
};

export const readMetric = (
    metric: MetricDef,
    coach: CoachRow,
    avg: number | null,
    canCompare: boolean,
    peers: CoachRow[] = []
): ReportMetric => {
    const value = metric.get(coach);
    const judgeable = metric.confidence !== 'REFERENCE';
    const prev = prevOf(coach, metric.key);
    const delta = value != null && prev != null ? round1(value - prev) : null;
    const improved =
        delta == null || delta === 0 ? null : metric.higherIsBetter ? delta > 0 : delta < 0;
    const ranked = canCompare ? rankOf(metric, coach, peers) : { rank: null, total: null };

    const base: Omit<ReportMetric, 'vsAvgPct' | 'ratio' | 'verdict' | 'reading'> = {
        key: metric.key,
        label: metric.label,
        unit: metric.unit,
        value,
        /*
         * 표시용으로 자릿수를 정리한다.
         * 수업량 평균처럼 나눗셈에서 온 값은 43.55555555555556 로 오고,
         * 이 값이 면담에 들고 나가는 PDF 에 그대로 인쇄된다.
         * 비교·판정은 아래에서 **원값 avg** 로 한다.
         */
        avg: avg == null ? null : round1(avg),
        higherIsBetter: metric.higherIsBetter,
        confidence: metric.confidence,
        judgeable,
        rank: ranked.rank,
        rankTotal: ranked.total,
        prev,
        delta,
        improved,
        basis: basisOf(coach, metric.key),
        why: metric.why,
    };

    if (value == null) {
        return {
            ...base,
            vsAvgPct: null,
            ratio: null,
            verdict: 'UNKNOWN',
            reading: '아직 집계되지 않는 지표입니다.',
        };
    }

    if (avg == null || avg === 0 || !canCompare) {
        return {
            ...base,
            vsAvgPct: null,
            ratio: null,
            verdict: 'UNKNOWN',
            reading: `${value}${metric.unit} 기록했습니다. 비교할 기준이 아직 없어 우열은 판단하지 않았습니다.`,
        };
    }

    const raw = (value - avg) / avg;
    const score = metric.higherIsBetter ? raw : -raw;
    const verdict: MetricVerdict =
        score >= MEANINGFUL_GAP ? 'GOOD' : score <= -MEANINGFUL_GAP ? 'BAD' : 'FLAT';

    /*
     * 문장이 어색하면 읽는 사람이 숫자를 안 믿습니다.
     * "0.4배만큼 낮습니다"는 한국어가 아닙니다.
     * 배수는 방향에 따라 표현을 다르게 씁니다.
     */
    const ratio = round1(value / avg);
    // 문장에 박히는 평균도 자릿수를 정리한다 (배수는 원값으로 구한 뒤다)
    const shownAvg = round1(avg);
    const gapText =
        ratio >= 1.15
            ? `센터 평균 ${shownAvg}${metric.unit}보다 ${ratio}배 높습니다`
            : ratio <= 0.85
              ? `센터 평균 ${shownAvg}${metric.unit}의 ${ratio}배 수준입니다`
              : `센터 평균 ${shownAvg}${metric.unit}과 비슷합니다`;

    return {
        ...base,
        vsAvgPct: Math.round(raw * 1000) / 10,
        ratio,
        verdict,
        reading: `${value}${metric.unit} — ${gapText}.`,
    };
};

export const readAllMetrics = (
    coach: CoachRow,
    avg: CenterAvg,
    canCompare: boolean,
    peers: CoachRow[] = []
): ReportMetric[] => {
    const avgOf = (key: MetricKey): number | null => {
        switch (key) {
            case 'conversion':
                return avg.conversionPct;
            case 'closing':
                return avg.closingRate;
            case 'response':
                return avg.responseHours;
            case 'sameDayCancel':
                return avg.sameDayCancel;
            case 'journal':
                return avg.journalPct;
            case 'journalRead':
                return avg.journalReadPct;
            case 'noshow':
                return avg.noshowPct;
            case 'injury':
                return avg.injuryPct;
            case 'lessonDensity':
                return lessonDensityAvgOf(avg, coach) ?? null;
            default:
                return null;
        }
    };
    return METRICS.map((m) => readMetric(m, coach, avgOf(m.key), canCompare, peers));
};

/* ═══════════════════════════════════════════════════════════
   대화 포인트 — 리포트의 존재 이유
   ═══════════════════════════════════════════════════════════ */

export type TalkingPoint = {
    kind: 'PRAISE' | 'ASK' | 'AGREE';
    /** 무엇에 관한 이야기인가 */
    headline: string;
    /** 실제로 던질 질문. 원장이 그대로 읽어도 되게 */
    question: string;
    /** 근거 숫자 — 질문에 힘을 싣는다 */
    basis: string;
};

/**
 * 면담에서 꺼낼 이야기.
 *
 * 지적이 아니라 **질문**입니다. "노쇼율이 높습니다"는 대화가 아니라 통보입니다.
 * "예약 시간대를 바꿔볼 여지가 있을까요?"여야 강사가 답을 갖고 옵니다.
 *
 * 잘한 것을 먼저 놓습니다. 개선점부터 꺼내면 나머지가 안 들립니다.
 */
export const buildTalkingPoints = (
    metrics: ReportMetric[],
    maturity: MaturityResult
): TalkingPoint[] => {
    const points: TalkingPoint[] = [];

    if (maturity.level === 'NEW') {
        points.push({
            kind: 'AGREE',
            headline: '무엇부터 쌓을지 정하기',
            question: '이번 달에 담당 회원을 몇 명까지 맡아 보시겠어요?',
            basis: '아직 수업 기록이 없어 성과를 볼 수 없습니다.',
        });
        return points;
    }

    const judged = metrics.filter((m) => m.judgeable && m.verdict !== 'UNKNOWN');
    const strengths = judged.filter((m) => m.verdict === 'GOOD').slice(0, 2);
    const gaps = judged.filter((m) => m.verdict === 'BAD').slice(0, 2);

    for (const m of strengths) {
        points.push({
            kind: 'PRAISE',
            headline: `${m.label}이 좋습니다`,
            question: `${m.label}을 어떻게 관리하고 계신지 팀에 공유해 주실 수 있을까요?`,
            basis: m.reading,
        });
    }

    for (const m of gaps) {
        points.push({
            kind: 'ASK',
            headline: `${m.label}을 함께 볼 부분`,
            // 지표마다 "무엇이 걸림돌일 수 있는지"가 달라 질문도 달라야 한다
            question: QUESTION_BY_METRIC[m.key] ?? `${m.label}에서 걸리는 부분이 무엇인가요?`,
            basis: m.reading,
        });
    }

    if (maturity.level === 'THIN') {
        points.push({
            kind: 'AGREE',
            headline: '다음 달에 같이 볼 것',
            question: '다음 달에는 어떤 지표를 기준으로 이야기 나눌지 정해볼까요?',
            basis: maturity.note,
        });
    }

    // 아무 이야깃거리도 없으면 리포트가 빈 종이가 된다.
    // 평균 수준이라는 것도 확인할 만한 사실이다.
    // 다만 **판정이 하나라도 나왔을 때만** 그렇게 말할 수 있다.
    // 하나도 못 쟀는데 "모든 지표가 센터 평균과 비슷"이라고 쓰면 지어낸 근거가 된다.
    if (!points.length) {
        points.push({
            kind: 'AGREE',
            headline: judged.length ? '눈에 띄는 변화 없음' : '이번 달은 판단을 보류했습니다',
            question: '이번 달에 특별히 어려웠던 점이 있으셨나요?',
            basis: judged.length
                ? '모든 지표가 센터 평균과 비슷한 수준입니다.'
                : '센터 평균과 맞대볼 수 있는 지표가 없어 우열을 판단하지 않았습니다.',
        });
    }

    return points;
};

/** 지표마다 걸림돌이 다르므로 질문도 달라야 한다 */
const QUESTION_BY_METRIC: Partial<Record<MetricKey, string>> = {
    conversion:
        '수강권이 끝나가는 회원에게 언제쯤 다음 이야기를 꺼내고 계신가요? 시점을 앞당겨 볼 여지가 있을까요?',
    closing: '체험·OT에서 결제까지 가는 과정 중 어디가 가장 어려우신가요?',
    response: '예약 요청 알림이 제때 오고 있나요? 알림 설정을 함께 확인해 볼까요?',
    noshow: '노쇼가 잦은 회원의 수업 시간대에 공통점이 있을까요? 요일이나 시간을 바꿔볼 여지가 있을까요?',
    lessonDensity:
        '담당 회원 수에 비해 수업이 적게 잡히고 있는데, 일정을 잡기 어려운 이유가 있으실까요?',
    journal: '수업 후 일지를 남길 시간이 부족하신가요? 어떤 방식이면 수월할까요?',
    journalRead: '회원이 일지를 잘 열어보게 하려면 어떤 내용이 들어가면 좋을까요?',
    sameDayCancel: '당일 취소가 회원 요청인지 일정 조정인지 구분해서 남겨 주실 수 있을까요?',
};

/* ═══════════════════════════════════════════════════════════
   다음 달 목표
   ═══════════════════════════════════════════════════════════ */

export type ReportGoal = {
    label: string;
    /** 왜 이 목표인가 — 근거 없는 목표는 지켜지지 않는다 */
    why: string;
};

export const buildGoals = (
    coach: CoachRow,
    metrics: ReportMetric[],
    maturity: MaturityResult
): ReportGoal[] => {
    if (maturity.level === 'NEW') {
        return [
            { label: '담당 회원 첫 수업 일정 잡기', why: '수업이 시작돼야 어떤 지표든 볼 수 있습니다.' },
            { label: '수업 후 일지 남기기', why: '일지가 쌓여야 회원과의 소통 상태를 확인할 수 있습니다.' },
        ];
    }

    const goals: ReportGoal[] = [];
    const gaps = metrics.filter((m) => m.judgeable && m.verdict === 'BAD');

    for (const m of gaps.slice(0, 2)) {
        if (m.avg == null) continue;
        goals.push({
            label: m.higherIsBetter
                ? `${m.label} ${m.avg}${m.unit} 이상으로 올리기`
                : `${m.label} ${m.avg}${m.unit} 이하로 낮추기`,
            why: m.reading,
        });
    }

    // 첫 수강권 회원이 있으면 그게 늘 가장 중요한 목표다
    if (coach.firstPassMembers > 0) {
        goals.push({
            label: `첫 수강권 회원 ${coach.firstPassMembers}명, 잔여 3회 시점에 상담하기`,
            /*
             * "가장 높은 구간"이라 쓰지 않는다 — 실측상 1번째와 2번째가
             * 사실상 같다. 숫자는 상수에서 읽는다. 강사 면담 자료에
             * 들어가는 문장이라 근거 없는 단정이 제일 위험하다.
             */
            why: `첫 수강권 회원의 ${FIRST_PASS_CHURN_RATE}%가 다음 수강권 없이 떠납니다.`,
        });
    }

    if (!goals.length) {
        /*
         * 비교를 못 했으면 "처지는 지표가 없다"고 쓸 수 없다.
         * 없는 기준으로 합격 도장을 찍는 문장이 되고, 그게 목표란에 인쇄된다.
         */
        goals.push(
            maturity.canCompare
                ? { label: '지금 수준 유지하기', why: '센터 평균 대비 뚜렷하게 처지는 지표가 없습니다.' }
                : {
                      label: '다음 달 비교가 가능하도록 기록 채우기',
                      why: '아직 센터 평균과 맞대볼 지표가 모자라 우열을 판단하지 않았습니다.',
                  }
        );
    }

    return goals.slice(0, 3);
};

/* ═══════════════════════════════════════════════════════════
   관찰 계획 — 데이터가 적을 때
   ═══════════════════════════════════════════════════════════ */

export type Observation = {
    what: string;
    why: string;
    /** 무엇이 쌓여야 볼 수 있는가 */
    needs: string;
};

/**
 * "지금은 못 보지만 다음 달엔 볼 수 있는 것".
 *
 * 데이터가 적은 센터에 빈 리포트를 주면 기능이 쓸모없어 보입니다.
 * 대신 무엇이 쌓이면 무엇을 볼 수 있는지를 적어 주면,
 * 원장도 강사도 지금 무엇을 해야 하는지 알게 됩니다.
 */
export const buildObservationPlan = (
    coach: CoachRow,
    metrics: ReportMetric[]
): Observation[] => {
    const plan: Observation[] = [];

    const unknown = metrics.filter((m) => m.judgeable && m.verdict === 'UNKNOWN');
    for (const m of unknown.slice(0, 3)) {
        plan.push({
            what: m.label,
            why: m.value == null ? '아직 집계되지 않습니다.' : '비교할 기준이 아직 없습니다.',
            needs:
                m.key === 'conversion'
                    ? '수강권이 정상 종료된 회원이 생기면 볼 수 있습니다.'
                    : m.key === 'closing'
                      ? '체험·OT 기회가 배정되면 볼 수 있습니다.'
                      : '기록이 한 달 이상 쌓이면 볼 수 있습니다.',
        });
    }

    if (judgementMembers(coach) < MIN_MEMBERS_FOR_COMPARE) {
        plan.push({
            what: '센터 평균과의 비교',
            why: `${membersLabelOf(coach)}이 ${judgementMembers(coach)}명이라 우연에 좌우됩니다.`,
            needs: `담당 회원 ${MIN_MEMBERS_FOR_COMPARE}명부터 비교를 시작합니다.`,
        });
    }

    return plan;
};

/* ═══════════════════════════════════════════════════════════
   전체 구성
   ═══════════════════════════════════════════════════════════ */

/**
 * 종합 등급.
 *
 * 인사 문서는 등급이 있어야 대화가 시작됩니다. "잘하고 있네요"로는
 * 다음 달에 무엇이 달라져야 하는지가 안 남습니다.
 *
 * 다만 **판정 가능한 지표로만** 매깁니다. 참고용 지표(당일취소·일지 등)나
 * 표본이 없는 지표로 등급을 매기면 사람이 잘못 평가됩니다.
 */
export type OverallGrade = 'S' | 'A' | 'B' | 'C' | 'NA';

export const GRADE_META: Record<OverallGrade, { label: string; desc: string }> = {
    S: { label: 'S', desc: '센터 기준을 여러 항목에서 크게 넘어섰습니다' },
    A: { label: 'A', desc: '강점이 보완점보다 많습니다' },
    B: { label: 'B', desc: '센터 평균 수준으로 운영하고 있습니다' },
    C: { label: 'C', desc: '보완이 필요한 항목이 강점보다 많습니다' },
    /*
     * "표본이 부족해"라고 못 박지 않는다.
     * NA 로 떨어지는 이유는 둘이다 — 담당 회원이 적거나, **센터 평균이 아직 없거나.**
     * 서버가 centerAvg 를 못 보낸 경우까지 "표본 부족"으로 인쇄하면
     * 원장이 있지도 않은 문제(회원 수)를 붙든다. 정확한 이유는 maturity.note 에 적힌다.
     */
    NA: { label: '—', desc: '비교할 기준이 모자라 등급을 매기지 않았습니다' },
};

export const gradeOf = (
    strengths: ReportMetric[],
    gaps: ReportMetric[],
    canCompare: boolean,
    /**
     * 실제로 판정이 나온 지표 수(verdict 가 UNKNOWN 이 아닌 것).
     *
     * 넘기면 0일 때 등급을 매기지 않습니다. 강점도 보완도 없는 상태는
     * 두 가지 뜻이 겹칩니다 — "다 평균 수준이다"(B)와 "하나도 못 쟀다"(NA).
     * 이 값이 둘을 가릅니다. 안 넘기면 예전대로 동작합니다.
     */
    judgedCount?: number
): OverallGrade => {
    // 비교할 수 없으면 등급도 없다. 없는 기준으로 사람을 평가하면 안 된다.
    if (!canCompare) return 'NA';
    /*
     * 판정된 지표가 하나도 없으면 B('센터 평균 수준으로 운영하고 있습니다')가 아니다.
     * 센터 평균이 통째로 없으면 모든 verdict 가 UNKNOWN 이 되는데,
     * 예전에는 strengths·gaps 가 비었다는 이유로 그대로 B 가 찍혔다.
     * "잴 게 없었다"를 "평균 수준이다"로 인쇄하면 안 된다.
     */
    if (judgedCount === 0) return 'NA';
    if (!strengths.length && !gaps.length) return 'B';
    if (gaps.length === 0 && strengths.length >= 3) return 'S';
    if (strengths.length > gaps.length) return 'A';
    if (gaps.length > strengths.length) return 'C';
    return 'B';
};

export type ReportPlan = {
    maturity: MaturityResult;
    /** 종합 등급 */
    grade: OverallGrade;
    /** 첫 장 한 줄 요약 */
    headline: string;
    metrics: ReportMetric[];
    /** 평가 근거로 쓸 수 있는 것만 */
    judgeable: ReportMetric[];
    strengths: ReportMetric[];
    gaps: ReportMetric[];
    talkingPoints: TalkingPoint[];
    goals: ReportGoal[];
    observationPlan: Observation[];
    /** 담당 회원 명부 — 이름이 있어야 대화가 행동이 된다 */
    memberBook: MemberBook | null;
    /** 지시 이행 — ⚠️ 달 구분이 없다. `orders` 주석 참고 */
    orderRecord: OrderRecord | null;
    /** 이 리포트에서 뺀 것 — 왜 없는지 밝혀야 신뢰가 남는다 */
    omitted: string[];
};

export type ComposeInput = {
    /** 담당 회원 (전체 회원을 넘겨도 이 강사 것만 골라 씁니다) */
    members?: MemberView[];
    /**
     * 지시 (전체를 넘겨도 이 강사 것만 골라 씁니다).
     *
     * ⚠️ **달로 걸러지지 않습니다** (2026-08-31 QA18에서 확인).
     *    서버가 `sentAt` 내림차순 최근 100건을 줄 뿐 month 파라미터가 없고,
     *    프론트도 `coachId` 와 `status` 로만 거릅니다. 그래서 7월 리포트에
     *    8월 지시가 그대로 섞입니다.
     *    → 문구에서 「이번 달」이라고 말하면 안 됩니다.
     *    → 진짜 월 스코프는 서버 계약 변경이 필요합니다
     *      (BACKEND_HANDOFF_OPS_REPORT_MONTH_SCOPE.md).
     */
    orders?: OrderDto[];
};

export const composeReport = (
    coach: CoachRow,
    avg: CenterAvg,
    allCoaches: CoachRow[],
    input: ComposeInput = {}
): ReportPlan => {
    // 성숙도를 알아야 비교 여부가 정해지고, 비교 여부에 따라 읽기가 달라진다.
    // 그래서 한 번 임시로 읽어 판정 가능한 지표 수를 센 뒤 다시 읽는다.
    const probe = readAllMetrics(coach, avg, true, allCoaches);
    /*
     * '판정 가능한 지표'는 값이 있는 것이 아니라 **비교 기준까지 있는** 것이다.
     *
     * 예전에는 `m.value != null` 만 셌다. 그러면 서버가 centerAvg 를 안 보낸 경우
     * (또는 아직 안 온 경우) 강사 쪽 값만 보고 judgeableCount 가 4까지 올라가
     * RICH · canCompare=true 가 됐다. 그 상태로 읽으면 모든 verdict 가 UNKNOWN 인데
     * 등급만 B('센터 평균 수준으로 운영하고 있습니다')로 찍혔다 — 기준이 없는데
     * 평균 수준이라고 인쇄한 것이다.
     *
     * probe 는 canCompare=true 로 읽으므로 verdict !== 'UNKNOWN' 은
     * "값도 있고 기준도 있다"와 같은 뜻이다. 이걸 센다.
     */
    const judgeableCount = probe.filter((m) => m.judgeable && m.verdict !== 'UNKNOWN').length;
    const maturity = resolveMaturity(coach, judgeableCount, allCoaches.length);

    const metrics = readAllMetrics(coach, avg, maturity.canCompare, allCoaches);
    const judgeable = metrics.filter((m) => m.judgeable && m.verdict !== 'UNKNOWN');
    const strengths = judgeable.filter((m) => m.verdict === 'GOOD');
    const gaps = judgeable.filter((m) => m.verdict === 'BAD');

    /*
     * 총평.
     * 마지막 갈래('큰 변화 없이 안정적으로')는 **판정이 실제로 나온 뒤**에만 쓴다.
     * 판정된 지표가 0개인데 그 문장을 쓰면, 기준이 없어서 아무것도 못 잰 강사가
     * 리포트 첫 장에서 "안정적으로 운영 중"으로 읽힌다. 등급은 NA 인데 총평만
     * 칭찬이면 원장은 둘 중 어느 쪽도 안 믿는다.
     */
    const headline =
        maturity.level === 'NEW'
            ? '아직 평가할 기록이 없습니다'
            : gaps.length
              ? `${gaps[0].label} — ${gaps[0].reading}`
              : strengths.length
                ? `${strengths[0].label} — ${strengths[0].reading}`
                : judgeable.length
                  ? '큰 변화 없이 안정적으로 운영하고 있습니다.'
                  : '센터 평균과 맞대볼 수 있는 지표가 없어 이번 달은 우열을 판단하지 않았습니다.';

    const omitted: string[] = [];
    if (!maturity.canCompare) omitted.push('센터 평균 대비 순위·우열 판단');
    // 기준선이 통째로 없으면 그 사실 자체를 밝힌다. 지표가 비어서인지
    // 기준이 비어서인지 구분이 안 되면 원장이 데이터를 못 믿는다.
    if (!metrics.some((m) => m.avg != null)) omitted.push('센터 평균 (아직 집계 전)');
    const notMeasured = metrics.filter((m) => m.value == null).map((m) => m.label);
    if (notMeasured.length) omitted.push(`아직 집계되지 않는 지표: ${notMeasured.join(', ')}`);

    const memberBook = input.members ? buildMemberBook(input.members, coach.coachId) : null;
    const orderRecord = input.orders ? buildOrderRecord(input.orders, coach.coachId) : null;

    /*
     * 대화 순서가 중요합니다.
     * 지시가 안 돌아가고 있으면 전환율 이야기는 소용이 없습니다.
     * 밀린 지시를 맨 앞에 놓습니다.
     */
    const orderPoint = orderRecord ? orderTalkingPoint(orderRecord) : null;
    const talkingPoints = buildTalkingPoints(metrics, maturity);
    if (orderPoint) {
        if (orderPoint.kind === 'ASK') talkingPoints.unshift(orderPoint);
        else talkingPoints.push(orderPoint);
    }

    // 챙길 회원이 있으면 그게 곧 다음 달 목표다
    const goals = buildGoals(coach, metrics, maturity);
    if (memberBook && memberBook.priority.length > 0) {
        goals.unshift({
            label: `이탈 위험·골든타임 회원 ${memberBook.priority.length}명 이번 달 안에 연락하기`,
            why: `${memberBook.priority
                .slice(0, 3)
                .map((m) => m.name)
                .join(', ')}${memberBook.priority.length > 3 ? ' 외' : ''} — 지금 놓치면 되돌리기 어렵습니다.`,
        });
    }

    return {
        maturity,
        grade: gradeOf(strengths, gaps, maturity.canCompare, judgeable.length),
        headline,
        metrics,
        judgeable,
        strengths,
        gaps,
        talkingPoints,
        goals: goals.slice(0, 4),
        observationPlan: buildObservationPlan(coach, metrics),
        memberBook,
        orderRecord,
        omitted,
    };
};

/* ═══════════════════════════════════════════════════════════
   담당 회원 명부
   ═══════════════════════════════════════════════════════════ */

/**
 * 리포트에 회원 명부가 왜 필요한가.
 *
 * 지표만 있는 리포트는 "전환율이 낮네요"에서 끝납니다.
 * 정작 면담에서 필요한 말은 "박서준 회원 19일째 안 오는데 어떻게 하실 건가요"입니다.
 * 이름이 있어야 대화가 행동으로 이어집니다.
 */
export type MemberBrief = {
    memberId: string;
    name: string;
    /** 상품명 — 'PT 20회'. 어떤 수강권인지 알아야 대화가 구체적이 된다 */
    product: string;
    /** '1번째' / '2번째 이상' */
    passOrderLabel: string;
    stage: MemberStage;
    stageLabel: string;
    remain: number | null;
    /**
     * 회차 개념이 없는 수강권인가 (무제한·대관).
     * `remain` 이 999 로 오는 상품이라 이 깃발 없이는 리포트가
     * "잔여 999회" 를 적고 미사용 선수금에 수천만 원을 더한다.
     */
    sessionless: boolean;
    expiryDday: number | null;
    paceNow: number;
    paceBefore: number;
    paceDeltaPct: number | null;
    lastLessonDaysAgo: number | null;
    /**
     * 미사용 선수금(원) — 잔여 회차 × 회당 단가. 못 세면 null.
     *
     * 🔴 리포트의 「걸린 금액」 칸이 이 값입니다 (2026-08-31 대표 지시).
     *    예전에는 `valueAmount` 하나였고 그 안에 재등록 기대가 섞여 있었습니다.
     *    강사에게 건네는 종이에 "이 회원에게 82만원이 걸려 있다"고 적혀 있는데
     *    그중 절반이 아직 안 들어온 기댓값이면, 강사는 그 돈을 이미 있는 돈으로
     *    읽고 원장과 다른 숫자를 들고 면담에 앉습니다.
     */
    prepaid: number | null;
    /** 재등록 기대(원). 기대하지 않는 자리(이탈 위험·주의)면 0, 못 세면 null */
    expected: number | null;
    /**
     * 이 회원이 다음 수강권으로 이어질 확률(%).
     * CHURN_RATE_BY_ORDER 의 여집합입니다. 숫자는 실측으로 갱신됩니다.
     * 순번을 확정할 수 없으면 null.
     */
    reRegistrationRate: number | null;
    /** 재등록 확률이 이 선 아래면 '위험'으로 본다 */
    reRegistrationAtRisk: boolean;
    /** 왜 이 회원이 목록에 있는가 — 한 줄 */
    note: string;
};

/**
 * 재등록 위험선.
 *
 * 의도는 그대로다 — **첫 수강권 회원은 잘 나와도 재등록 위험 목록에
 * 올린다.** 다만 기준 숫자가 실측으로 갱신됐다. 옛 값(재등록률 31.7%)
 * 기준의 40 을 그대로 두면 새 실측(1번째 52.9%)에서 첫 수강권 회원이
 * 한 명도 안 걸려 목록이 비어 버린다.
 *
 * 55 로 잡으면 1번째(52.9)와 2번째(53.4)가 걸리고 3번째(63.5)는
 * 빠진다. 실측상 첫 두 수강권의 이탈률이 사실상 같으므로(47.1 vs
 * 46.6) 2번째가 함께 걸리는 것은 부작용이 아니라 데이터가 말하는
 * 그대로다. CHURN_RATE_BY_ORDER 를 재계산하면 이 선도 같이 봐야 한다.
 */
export const RE_REGISTRATION_RISK_LINE = 55;

/** 급한 순서 — 숫자가 클수록 먼저 */
export const urgencyOf = (m: MemberBrief): number => {
    const stageWeight: Record<MemberStage, number> = {
        AT_RISK: 3000,
        GOLDEN: 2000,
        FIRST_PASS: 1000,
        /* 첫 수강권보다 뒤, 안정보다 앞. 「아직 괜찮다」는 뜻이라 위로 올리지 않는다 */
        WATCH: 500,
        STABLE: 0,
    };
    const gap = m.lastLessonDaysAgo ?? 0;
    // 잔여가 적을수록 급하다. 모르면 급하지 않은 쪽으로 둔다.
    const remainPressure = m.remain == null ? 0 : Math.max(0, 20 - m.remain);
    return stageWeight[m.stage] + gap * 5 + remainPressure;
};

const noteOf = (m: MemberView, stage: MemberStage): string => {
    if (stage === 'AT_RISK') {
        const gap = m.lastLessonDaysAgo;
        return m.paceDeltaPct != null
            ? `수업 속도 ${Math.abs(m.paceDeltaPct)}% 감소 · ${gap}일 공백`
            : `${gap}일째 수업 없음`;
    }
    if (stage === 'GOLDEN') {
        /* 무제한(대관)에 "잔여 999회" 를 적지 않는다 — 남은 기준은 만료일뿐이다 */
        const parts = [
            isSessionless(m) ? '회차 제한 없음' : m.remain != null ? `잔여 ${m.remain}회` : null,
            m.expiryDday != null && m.expiryDday >= 0 ? `만료 D-${m.expiryDday}` : null,
        ].filter(Boolean);
        return `${parts.join(' · ')} — 재등록 상담 시점`;
    }
    if (stage === 'FIRST_PASS') return '첫 수강권 구간 · 이탈률이 가장 높습니다';
    /*
     * 🔴 주의는 **왜 여기 있는지**를 적어야 한다.
     *    "페이스 유지 중"으로 뭉개면 안정 궤도와 구분이 안 되고,
     *    강사가 받아 든 명부에서 이 회원만 이유 없이 서 있게 된다.
     */
    if (stage === 'WATCH') {
        const reasons = watchReasons(m).map((r) => WATCH_REASON_TEXT[r]);
        return reasons.length > 0 ? reasons.join(' · ') : '안정 기준에 못 미침';
    }
    return '페이스 유지 중';
};

/**
 * 다음 수강권으로 이어질 확률.
 *
 * 순번이 하한선이거나(재등록으로 유입) 검증 구간 밖이면 확정값처럼 쓰지 않고
 * 3번째 값을 보수적으로 적용합니다 — 위험을 과대평가하지 않기 위해서입니다.
 */
export const reRegistrationRateOf = (m: MemberView): number | null => {
    const order = m.passOrder;
    if (order.basis === 'NONE') return null;
    const known = REREGISTRATION_RATE_BY_ORDER[order.order];
    if (known != null && !order.isAtLeast) return known;
    return known ?? FALLBACK_REREGISTRATION_RATE;
};

export const toMemberBrief = (m: MemberView): MemberBrief => ({
    memberId: m.memberId,
    name: m.name,
    product: m.product,
    passOrderLabel: m.passOrder.label,
    stage: m.stage,
    stageLabel: STAGE_META[m.stage].label,
    remain: m.remain,
    sessionless: isSessionless(m),
    expiryDday: m.expiryDday,
    paceNow: m.paceNow,
    paceBefore: m.paceBefore,
    paceDeltaPct: m.paceDeltaPct,
    lastLessonDaysAgo: m.lastLessonDaysAgo,
    prepaid: m.value?.prepaid ?? null,
    expected: m.value?.expected ?? null,
    reRegistrationRate: reRegistrationRateOf(m),
    reRegistrationAtRisk: (reRegistrationRateOf(m) ?? 100) < RE_REGISTRATION_RISK_LINE,
    note: noteOf(m, m.stage),
});

export type MemberBook = {
    total: number;
    /**
     * 담당 회원 수업의 주차별 합계 (오래된 주 → 최근 주).
     * 강사 단위 수업이 늘고 있는지 줄고 있는지는 총량만 봐서는 안 보입니다.
     */
    weeklyLessons: number[];
    /**
     * 각 주가 며칠부터 며칠까지인가.
     * "8주 전"만 적으면 언제 이야기인지 모릅니다. 면담에서 "그 주에 무슨 일이
     * 있었죠?"를 물으려면 날짜가 있어야 합니다.
     */
    weeklyRanges: { label: string; from: string; to: string }[];
    byStage: Record<MemberStage, MemberBrief[]>;
    /** 이번 달 안에 반드시 챙겨야 하는 회원 */
    priority: MemberBrief[];
    /*
     * 🔴 `defendTotal`(이탈위험+첫수강권) · `gainTotal`(골든타임+안정) 을 없앴습니다
     *    (2026-08-31 정책 M-D1).
     *
     * 단계로 돈의 성격을 가르던 개념 자체가 폐기됐습니다. 두 필드는 화면·PDF
     * 어디에서도 렌더되지 않는 죽은 값이었는데, 남겨 두면 다음 사람이 그 위에
     * 계산을 얹습니다. 대신할 값은 이미 아래 둘입니다 —
     * `unusedPrepaid`(= 전 단계 Σ 미사용 선수금)와 `expectedReRegistrationRevenue`.
     */
    /** 금액을 계산하지 못한 회원 수 */
    unpriced: number;
    /**
     * 재등록 확률이 위험선 아래인 회원.
     *
     * 이 기능의 근거가 된 검증에서 가장 강했던 신호가 수강권 순번입니다
     * (순번별 실측치는 passOrder.CHURN_RATE_BY_ORDER 참조).
     * 지금 잘 나오고 있어도 첫 수강권이면 열에 일곱이 안 돌아옵니다.
     * 페이스가 꺾이기 전에 알아야 손을 쓸 수 있습니다.
     */
    reRegistrationRisk: MemberBrief[];
    /** 그 회원들에게 걸린 금액 */
    reRegistrationRiskValue: number;

    /**
     * 곧 끝나는 회원.
     *
     * 만료일만 보면 안 됩니다. 20회권을 사놓고 2회 남은 회원은
     * 만료가 두 달 남았어도 **다음 주에 끝납니다.**
     * 기간과 회차 둘 중 하나라도 임박하면 여기에 들어옵니다.
     */
    endingSoon: MemberBrief[];

    /**
     * 미사용 선수금 — 이미 받았지만 아직 수업하지 않은 금액.
     *
     * 회계로는 부채(이연수익)입니다. 이 회원들이 안 나오면 환불 요구가 되고,
     * 다 쓰면 재등록 기회가 됩니다. 그래서 원장이 매달 봐야 하는 숫자입니다.
     */
    unusedPrepaid: number;

    /**
     * 다음 달 재등록 예상 매출.
     *
     * 곧 끝나는 회원 각각에 대해
     *   다음 수강권 예상 결제액 × 그 회원의 재등록 확률
     * 을 더한 값입니다. 확률을 곱한 기댓값이라 확정 매출이 아닙니다.
     */
    expectedReRegistrationRevenue: number;
};

/** 기간이 이 안에 들어오면 임박으로 본다 */
export const ENDING_SOON_DAYS = 30;
/** 잔여 회차가 이 이하면 임박으로 본다 */
export const ENDING_SOON_SESSIONS = 4;

/**
 * PDF 명부에 싣는 단계 — **다섯 칸 전부.**
 *
 * 🔴 여기에 단계를 빠뜨리면 그 회원이 **인쇄물에서 통째로 사라집니다.**
 *    타입이 `MemberStage[]` 라 컴파일러가 빠진 값을 잡아 주지 않습니다 —
 *    `Record<MemberStage, …>` 와 달리 배열은 부족해도 통과합니다.
 *    강사에게 건네는 명부에서 회원이 조용히 없어지는 자리라,
 *    합계 보존 테스트(reportComposer.test)가 이 배열을 따로 지켜봅니다.
 */
const STAGES: MemberStage[] = ['AT_RISK', 'GOLDEN', 'FIRST_PASS', 'WATCH', 'STABLE'];

/**
 * 담당 회원 명부.
 *
 * 금액은 아는 것만 더합니다. 단가가 없는 수강권(무료권·체험)을 0원으로 더하면
 * 합계는 같아 보이지만 그 회원이 "챙길 것 없는 사람"으로 묻힙니다.
 */
export const buildMemberBook = (members: MemberView[], coachId: string): MemberBook => {
    const mine = members.filter((m) => m.coachId === coachId).map(toMemberBrief);

    const byStage = STAGES.reduce(
        (acc, st) => {
            acc[st] = mine
                .filter((m) => m.stage === st)
                .sort((a, b) => urgencyOf(b) - urgencyOf(a));
            return acc;
        },
        {} as Record<MemberStage, MemberBrief[]>
    );

    /*
     * 회원별 주차 추이를 겹쳐 강사 단위 흐름을 만든다.
     * 길이가 다른 배열이 섞여도 가장 긴 것에 맞춰 0으로 채운다.
     */
    const raw = members.filter((m) => m.coachId === coachId).map((m) => m.trend ?? []);
    const weeks = raw.reduce((n, t) => Math.max(n, t.length), 0);
    const weeklyLessons = Array.from({ length: weeks }, (_, i) =>
        raw.reduce((s, t) => s + (t[i] ?? 0), 0)
    );

    /*
     * 재등록 위험 명단.
     * 확률이 낮은 순, 같으면 금액이 큰 순 — 먼저 손댈 사람이 위로 온다.
     */
    const reRegistrationRisk = mine
        .filter((m) => m.reRegistrationAtRisk)
        .sort((a, b) => {
            const ra = a.reRegistrationRate ?? 100;
            const rb = b.reRegistrationRate ?? 100;
            if (ra !== rb) return ra - rb;
            /* 확률이 같으면 걸린 선수금이 큰 쪽부터 — 먼저 손댈 사람이 위로 온다 */
            return (b.prepaid ?? 0) - (a.prepaid ?? 0);
        });

    /*
     * 곧 끝나는 회원 — 기간과 회차를 함께 본다.
     * 이미 만료된 회원은 뺀다. 지난 일은 다음 달 영업 대상이 아니다.
     */
    const endingSoon = mine
        .filter((m) => {
            const byDate = m.expiryDday != null && m.expiryDday >= 0 && m.expiryDday <= ENDING_SOON_DAYS;
            /* 무제한(대관)은 회차로 끝나지 않는다 — 만료일로만 걸린다 */
            const bySession =
                !m.sessionless && m.remain != null && m.remain <= ENDING_SOON_SESSIONS;
            return byDate || bySession;
        })
        .sort((a, b) => {
            // 회차가 적은 쪽이 먼저. 회차를 모르면 만료일로 본다.
            const ra = a.remain ?? 999;
            const rb = b.remain ?? 999;
            if (ra !== rb) return ra - rb;
            return (a.expiryDday ?? 999) - (b.expiryDday ?? 999);
        });

    const source = members.filter((m) => m.coachId === coachId);
    /*
     * 미사용 선수금 = Σ 계산기의 `prepaid` (정책 M8).
     *
     * ─────────────────────────────────────────────────────────────
     * 🔴 여기서 식을 다시 쓰지 않습니다 (2026-08-31)
     * ─────────────────────────────────────────────────────────────
     * 예전에는 이 자리에서 `잔여 × 단가` 를 **직접 다시 계산**했습니다.
     * 계산기(`memberValue`)와 식이 같아 보였지만 규칙이 미묘하게 달랐습니다 —
     * 계산기는 0원·음수 단가를 「모른다」로 보고 빼는데(`priced()`), 여기서는
     * `null` 만 걸러서 0원 단가를 0으로 더했습니다.
     *
     * 그리고 검산 ①이 **「지켜야 할 매출 = 강사별 미사용 선수금의 합」**(M8=M2)을
     * 요구합니다. 두 경로가 각자 계산하면 그 등식이 언젠가 조용히 깨지고,
     * 깨진 날 원장님은 화면 두 곳에서 다른 금액을 봅니다.
     * 그래서 **계산기 한 곳**만 봅니다.
     *
     * 무제한(대관) 제외도 계산기가 이미 합니다 — 그 경우 `value` 자체가 null 입니다.
     */
    const unusedPrepaid = source.reduce((s, m) => s + (m.value?.prepaid ?? 0), 0);

    /*
     * 다음 달 재등록 예상 매출 — **계산기의 `expected` 를 그대로 쓴다** (정책 M3·M7).
     *
     * ─────────────────────────────────────────────────────────────
     * 🔴 여기서도 식을 다시 쓰고 있었다 (2026-08-31 교차검증에서 잡힘)
     * ─────────────────────────────────────────────────────────────
     * 예전 코드는 `expectedNextPayment × rate` 를 직접 다시 계산했다.
     * 그래서 정책 두 가지를 통째로 건너뛰었다.
     *
     *   ① **이탈 위험·주의 회원에게도 재등록률을 곱했다.** 화면은 그 회원의
     *      기대를 0 으로 두는데(M-D2) 강사에게 건네는 종이에는 기대액이 찍혔다.
     *   ② **회원당 한 번이 아니라 카드마다 더했다.** 「곧 끝나는 회원」의
     *      memberId 로 걸러 놓고 그 회원의 **다른 수강권까지 전부** 더했다.
     *
     * 실서버 실측(2026-08-31): 44,966,172원 → 26,056,432원. **1,891만원 과대**였다.
     *
     * 그래서 대표 수강권 한 장만 남기고(`pickPrimaryCards`) 그 카드의 `expected`
     * 를 더한다. 계산기를 거치므로 단계 게이트·무제한 제외·0원 단가 규칙이
     * 자동으로 따라온다 — `unusedPrepaid` 와 같은 이유다.
     */
    const endingIds = new Set(endingSoon.map((m) => m.memberId));
    const expectedReRegistrationRevenue = pickPrimaryCards(source)
        .filter((m) => endingIds.has(m.memberId))
        .reduce((s, m) => s + (m.value?.expected ?? 0), 0);

    /*
     * 주차 구간 라벨.
     * 마지막 칸이 이번 주(오늘 포함)이고 거슬러 올라간다.
     */
    const today = dayjs();
    const weeklyRanges = weeklyLessons.map((_, i) => {
        const back = weeklyLessons.length - 1 - i;
        const end = today.subtract(back * 7, 'day');
        const start = end.subtract(6, 'day');
        return {
            label: back === 0 ? '이번 주' : `${back}주 전`,
            from: start.format('M/D'),
            to: end.format('M/D'),
        };
    });

    return {
        total: mine.length,
        weeklyLessons,
        weeklyRanges,
        byStage,
        endingSoon,
        unusedPrepaid,
        expectedReRegistrationRevenue,
        reRegistrationRisk,
        /* 「걸린 금액」은 미사용 선수금이다 — 기대액을 섞지 않는다 (정책 M-D4) */
        reRegistrationRiskValue: reRegistrationRisk.reduce((s, m) => s + (m.prepaid ?? 0), 0),
        // 이탈 위험과 골든타임은 이번 달을 넘기면 늦는다
        priority: [...byStage.AT_RISK, ...byStage.GOLDEN].sort((a, b) => urgencyOf(b) - urgencyOf(a)),
        /*
         * 금액을 한 푼도 못 낸 회원 수.
         * `prepaid` 와 `expected` 가 **둘 다** null 일 때만 셉니다 —
         * 계산기가 null 을 돌려준 경우와 같은 뜻입니다.
         */
        unpriced: mine.filter((m) => m.prepaid == null && m.expected == null).length,
    };
};

/* ═══════════════════════════════════════════════════════════
   지시 이행
   ═══════════════════════════════════════════════════════════ */

/**
 * 이번 달 이 강사에게 나간 지시가 실제로 실행됐는가.
 *
 * 성과 지표보다 이게 먼저입니다. 지시가 안 돌아가고 있으면
 * 전환율 얘기를 해봐야 소용이 없습니다.
 */
export type OrderRecord = {
    total: number;
    done: number;
    /** 실행률(%). 지시가 없었으면 null — 0% 는 "다 안 했다"는 뜻이라 다르다 */
    ratePct: number | null;
    /** 아직 안 된 것 — 면담에서 하나씩 물어볼 거리 */
    pending: { memberName: string; task: string; daysAgo: number; valueAmount: number }[];
    /** 전체 지시 목록 — 리포트에 그대로 싣는다 */
    all: {
        memberName: string;
        task: string;
        daysAgo: number;
        done: boolean;
        valueAmount: number;
    }[];
    /** 밀린 지시에 걸린 금액 합계 — "3건 남았다"보다 "120만원이 걸려 있다"가 움직인다 */
    pendingValue: number;
    /**
     * 서버 상한(200건)에 걸렸을 수 있는가 — **이행률을 단정하면 안 되는 달**이다.
     *
     * 목록은 `sentAt` 내림차순 200건까지만 온다. `month` 로 걸러도 그 상한은
     * **필터 뒤에 남는다.** 한 달 지시가 200건을 넘는 센터에서는 오래된 건이
     * 잘리고, 잘린 건이 분모에서 빠져 이행률이 실제와 달라진다.
     *
     * 정확히 몇 건이 잘렸는지는 알 수 없다(서버가 총건수를 안 준다).
     * 그러니 고칠 수 없는 값이고, **밝히는 것이 유일한 정직**이다.
     */
    capped: boolean;
};

/**
 * 지시 목록 서버 상한. 이 수만큼 왔다면 더 있었을 수 있다.
 *
 * ⚠️ 서버 `opsBriefing.service.listOrders` 의 limit 과 같아야 한다.
 * 서버가 올리면 여기도 올려야 하고, 그때까지는 화면이 없는 경고를 띄운다.
 */
export const ORDER_LIST_CAP = 200;

const isDone = (o: OrderDto) => o.status === 'DONE' || o.status === 'ARCHIVED';

export const buildOrderRecord = (orders: OrderDto[], coachId: string): OrderRecord => {
    /*
     * 철회한 지시는 **분모에도 분자에도** 넣지 않는다.
     *
     * 이 리포트는 강사에게 건네는 문서다. 원장이 "이건 제가 잘못 보냈습니다"
     * 하고 취소한 건이 여기 '안 한 일'로 남으면, **원장의 실수가 강사의
     * 실행률을 깎는다.** 하지 말라고 해 놓고 안 했다고 셀 수는 없다.
     * (서버도 어제 지시 요약에서 같은 이유로 양쪽에서 뺀다)
     *
     * 지금 화면이 넘겨주는 목록에는 애초에 철회 건이 없다(서버 기본 목록이
     * 빼고 준다). 그래도 여기서 한 번 더 거르는 것은, 이 함수가 어떤 목록을
     * 받게 될지는 호출부가 정하기 때문이다 — includeArchived 로 부른 목록을
     * 누가 여기 물리면 그 순간 조용히 숫자가 틀어진다.
     */
    const mine = orders.filter((o) => o.coachId === coachId && o.status !== 'RETRACTED');
    const done = mine.filter(isDone);

    const all = [...mine]
        // 미실행이 위로, 그 안에서는 오래된 것부터 — 목록이 곧 처리 순서가 된다
        .sort((a, b) => {
            if (isDone(a) !== isDone(b)) return isDone(a) ? 1 : -1;
            return b.sentDayOffset - a.sentDayOffset;
        })
        .map((o) => ({
            memberName: o.memberName,
            task: o.task,
            daysAgo: o.sentDayOffset,
            done: isDone(o),
            valueAmount: o.valueAmount ?? 0,
        }));

    const pending = all.filter((o) => !o.done);

    return {
        total: mine.length,
        done: done.length,
        ratePct: mine.length ? Math.round((done.length / mine.length) * 1000) / 10 : null,
        pending,
        all,
        pendingValue: pending.reduce((s, o) => s + o.valueAmount, 0),
        /*
         * 센터 전체 응답이 상한에 닿았으면 이 강사 몫도 잘렸을 수 있다.
         * 이 강사 건수(mine)가 아니라 **받은 목록 전체**를 봐야 한다 —
         * 다른 강사 지시가 창을 채워도 내 것이 밀려나기 때문이다.
         */
        capped: orders.length >= ORDER_LIST_CAP,
    };
};

/**
 * 지시 이행에서 나오는 대화 거리.
 * 밀린 지시가 있으면 성과 이야기보다 이게 먼저 나와야 합니다.
 */
export const orderTalkingPoint = (record: OrderRecord): TalkingPoint | null => {
    if (!record.total) return null;
    /*
     * ⚠️ 강사 앱에는 이 지시를 받는 화면도 알림도 아직 없습니다.
     * 강사는 지시를 전달받은 적이 없으므로 "실행/미실행"으로 평가하면
     * 받은 적 없는 일로 사람을 나무라는 문장이 됩니다.
     * '확인'은 원장님이 처리 여부를 기록했다는 뜻이며, 그게 이 숫자의 실제 의미입니다.
     * (CoachPdfReport 의 '지시 이행' 섹션이 같은 말을 씁니다)
     */
    if (!record.pending.length) {
        return {
            kind: 'PRAISE',
            headline: '남긴 지시가 빠짐없이 처리됐습니다',
            /*
              🔴 「이번 달」을 뺐다 (QA18). 이 목록은 달로 걸러지지 않는다 —
                 7월 리포트를 열어도 8월 지시가 들어 있다. 「이번 달 지시가
                 모두 처리됐다」고 적으면, 실제로는 다른 달 것까지 세어 놓고
                 그 달을 다 끝냈다고 단언하는 문장이 된다.
            */
            question: '남긴 지시가 모두 처리됐습니다. 진행하면서 어려웠던 점이 있으셨나요?',
            basis: `지시 ${record.total}건 전부 처리 확인`,
        };
    }
    const oldest = record.pending[0];
    // "3건 남았다"보다 "120만원이 걸려 있다"가 사람을 움직인다
    const money = record.pendingValue
        ? ` · 걸린 금액 ${record.pendingValue.toLocaleString('ko-KR')}원`
        : '';
    return {
        kind: 'ASK',
        headline: '아직 처리되지 않은 지시',
        question: `${oldest.memberName} 회원 건이 ${oldest.daysAgo}일째 남아 있는데, 진행이 어려운 이유가 있을까요?`,
        basis: `지시 ${record.total}건 중 ${record.total - record.done}건 미확인 (원장님 기록 기준 확인율 ${record.ratePct}%)${money}`,
    };
};
